// Third Party
import * as $ from 'jquery';

// Event Bus
import EventBus from "@/app/bus/event-bus.js"

// Mixins
import { tableMixins } from "@/app/mixins/utils/table-mixins.js";
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { dateMixins } from "@/app/mixins/utils/date-mixins";
import { comingSoonMixins } from '@/app/mixins/utils/coming-soon-mixins';

// Services
import damSireService from "@/app/services/main/dam-sire/dam-sire-service.js";

// Components
import DatePicker from "vue2-datepicker"
const ModalAddEditDeleteDamSireDocument = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "modal-add-edit-delete-dam-sire-document" */ "@/app/views/main-view/breeding-management/dam-sire/components/modals/modal-add-edit-delete-dam-sire-document/modal-add-edit-delete-dam-sire-document.vue"
    );
const ModalViewDocument = () => import(/* webpackPrefetch: true, webpackChunkName: "modal-view-document" */ '@/app/views/main-view/general-info/modals/modal-view-document/modal-view-document.vue');

// Constants
import { STATUS_CODES, SUCCESS_MSG, DAM_SIRE_DOCUMENT_STATUS } from "@/app/constants/status-constants";
const ACTION_TYPES = {
    UPLOAD: "Upload",
    EDIT: "Edit",
    DELETE: "Delete",
}

// Third party libraries
import { format } from "date-fns";

export default {
    name: "DamSireDocumentsContainer",
    components: {
        DatePicker,
        ModalAddEditDeleteDamSireDocument,
        ModalViewDocument,
    },
    directive: {},
    filters: {},
    mixins: [tableMixins, toastMessageMixins, dateMixins, comingSoonMixins],
    props: {},
    data() {
        return {
            isAPILoading: false,
            isAPISubmitLoading: false,

            damSireId: null,
            damSireIdToDelete: null,
            damSireDetails: null,
            copyTextContent: "Copy to Clipboard",
            copyTimeout: null, // Store timeout ID for clearing

            dateRange: null,
            prevRoute: "",
            acceptedFileTypes: ["application/pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "image/png", "image/jpeg"],
            inProgressCount: null,

            theads: [
                {
                    name: "Description",
                    sortLabel: "description",
                    isDesc: false,
                    hasSearch: true,
                    searchLabel: "f_description",
                },
                {
                    name: "Type",
                    sortLabel: "fileType",
                    isDesc: false,
                    hasType: true,
                    searchLabel: "f_fileType",
                },
                {
                    name: "Name",
                    sortLabel: "fileName",
                    isDesc: false,
                    hasSearch: true,
                    searchLabel: "f_fileName",
                },
                {
                    name: "Date",
                    sortLabel: "createdAt",
                    isDesc: true,
                    hasScheduleSearch: true,
                    searchLabel: "f_createdAt",
                },
                { name: null, sortLabel: null, style: "width: 5%;" },
                { name: null, sortLabel: null, style: "width: 3%;" },
                { name: null, sortLabel: null, style: "width: 3%;" },
                { name: null, sortLabel: null, style: "width: 3%;" },
            ],

            docTypesList: [
                { name: "png", code: "png" },
                { name: "jpeg", code: "jpeg" },
                { name: "doc", code: "doc" },
                { name: "pdf", code: "pdf" },
            ],
        };
    },
    computed: {},
    watch: {},
    async created() {
        if (this.$route.query) {
            this.damSireId = this.$route.query.id;
        }

        if (this.$route.query.from === "dam-sire-list") {
            this.prevRoute = "Dam/Sire List";
        } else {
            this.prevRoute = `${this.damSireDetails ? this.damSireDetails.gender : ""} Details`;
        }

        await this.getTableContent(this.page, this.limit, this.sortBy, (this.desc = true));
        await this.getDamSireDetails();
        await this.getDamSireDocumentsListProgress();
    },
    methods: {
        onPickFile() {
            this.$refs.fileInput.click();
        },
        async onFilePicked(event) {
            const files = event.target.files;

            // return early to prevent unnecessary code execution
            if (!files || (files && !files.length)) {
                return;
            }

            // validate file if it's valid or doesn't exceed 20mb
            const isFileValid = await this.validateFileValidity(files[0]);

            if (!isFileValid) {
                return;
            }

            this.$refs.modalAddEditDeleteDamSireDocument.show(files[0], ACTION_TYPES.UPLOAD, this.damSireId);
        },
        async validateFileValidity(file) {
            const maxFileSize = 20 * 1024 * 1024; // 20 MB in bytes

            if (!file) {
                return false;
            }

            let errorMessage = "";
            let isValid = true;

            // by default file.size is in bytes
            if (file.size > maxFileSize) {
                errorMessage = "File size must not exceed 20 MB.";
                isValid = false;
            }

            const isFileTypeValid = this.acceptedFileTypes.includes(file.type);

            if (!isFileTypeValid) {
                errorMessage = `${file.name} has unsupported file format.`;
                isValid = false;
            }

            if (file.name && file.name.length > 255) {
                errorMessage = `File name is too long.`;
                isValid = false;
            }

            if (!isValid) {
                this.showCustomErrorToast("Unable to upload file.", errorMessage);
            }

            return isValid;
        },
        async getTableContent(page = this.page, limit = this.limit, sortBy = this.sortBy, desc = this.desc) {
            await this.getDocumentsList({ page, limit, sortBy, desc });
        },
        async getDocumentsList(queryParams) {
            try {
                this.isAPILoading = true;

                queryParams["f_damSireId"] = this.damSireId;

                if (this.columnsFilter["f_fileType"]) {
                    queryParams["f_fileType"] = this.columnsFilter["f_fileType"];
                }
                if (this.columnsFilter["f_fileName"]) {
                    queryParams["f_fileName"] = this.columnsFilter["f_fileName"];
                }
                if (this.columnsFilter["f_description"]) {
                    queryParams["f_description"] = this.columnsFilter["f_description"];
                }
                if (this.columnsFilter["f_createdAt"] && this.columnsFilter["f_createdAt"][0]) {
                    queryParams["f_startDate"] = format(new Date(this.columnsFilter["f_createdAt"][0]), "yyyy-MM-dd");
                    queryParams["f_endDate"] = format(new Date(this.columnsFilter["f_createdAt"][1]), "yyyy-MM-dd");
                }

                const response = await damSireService.getDamSireDocumentsList(queryParams);
                if (response.status === STATUS_CODES.SUCCESS) {
                    this.tableContent = {
                        tableData: response.data.data,
                        paginationData: response.data.pagination,
                    };
                } else if(detailsResponse.status === STATUS_CODES.UNAUTHORIZED){
                    EventBus.$emit('errorUnAuthorizeUser', true);
                }  else {
                    const { ERROR_GET_DAM_SIRE_DOCUMENTS_LIST } = this.toastMessages.damSireDocument();
                    this.showAPIErrorToast(ERROR_GET_DAM_SIRE_DOCUMENTS_LIST, response);
                }
            } catch (err) {
                this.showGenericErrorToast();
            } finally {
                this.isAPILoading = false;
            }
        },
        async getDamSireDocumentsListProgress() {
            try {
                const response = await damSireService.getDamSireDocumentsListProgress(this.damSireId, DAM_SIRE_DOCUMENT_STATUS.IN_PROGRESS);

                if (response.status === STATUS_CODES.SUCCESS) {
                    const { count: inProgressCount } = response.data;
                    this.inProgressCount = inProgressCount;
                } else if(detailsResponse.status === STATUS_CODES.UNAUTHORIZED){
                    EventBus.$emit('errorUnAuthorizeUser', true);
                }
            } catch (err) {
            }
        },
        async getDamSireDetails() {
            try {
                this.isAPILoading = true;
                const damSireId = this.$route.query.id;
                const detailsResponse = await damSireService.getDamSireDetails(damSireId);

                if (detailsResponse.status === STATUS_CODES.SUCCESS) {
                    this.damSireDetails = detailsResponse.data;
                } else if(detailsResponse.status === STATUS_CODES.UNAUTHORIZED){
                    EventBus.$emit('errorUnAuthorizeUser', true);
                } else {
                    const { ERROR_GET_DAM_SIRE } = this.toastMessages.damSire();
                    this.showAPIErrorToast(ERROR_GET_DAM_SIRE, detailsResponse);
                }
            } catch (err) {
                this.showGenericErrorToast();
            } finally {
                this.isAPILoading = false;
            }
        },
        async previewDocument(document) {
            const { fileURL, fileType, fileName } = document;

            const imageTypes = ["image/png", "image/jpeg", "image/jpg", "png", "jpg", "jpeg"];

            if (imageTypes.includes(fileType) || fileType === "pdf" || fileType === "application/pdf") {
                // format document to match the keys received by modalViewDocument component
                const formattedDocument = {
                    documentUrl: fileURL,
                    type: fileType,
                    name: fileName,
                };

                this.$refs.modalViewDocument.show(formattedDocument, imageTypes);
            } else {
                window.open(fileURL, "_blank"); // doc/docx will be downloaded automatically
            }
        },
        async showEditDocument(document) {
            this.$refs.modalAddEditDeleteDamSireDocument.show(document, ACTION_TYPES.EDIT, this.damSireId);
        },
        async showDeleteDocument(documentId) {
            const confirmDeleteResult = await this.$swal({
                title: "Delete Document",
                text: "Are you sure you want to delete the document?",
                showCancelButton: true,
                confirmButtonColor: "#4B8BFF",
                cancelButtonColor: "#d33",
                confirmButtonText: "Confirm",
                cancelButtonText: "Cancel",
                reverseButtons: true,
                showCloseButton: true,
            });

            if (confirmDeleteResult.isConfirmed) {
                this.damSireIdToDelete = documentId;

                try {
                    this.isAPISubmitLoading = true;

                    const documentDetails = { damSireId: this.damSireId };
                    const deletionResponse = await damSireService.deleteDamSireDocument(documentId, documentDetails);

                    const { SUCCESS_DELETE_DAM_SIRE_DOCUMENT, ERROR_DELETE_DAM_SIRE_DOCUMENT } = this.toastMessages.damSireDocument();

                    if (deletionResponse.status === STATUS_CODES.SUCCESS_NO_CONTENT) {
                        this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_DELETE_DAM_SIRE_DOCUMENT);
                        await this.getTableContent(this.page, this.limit, this.sortBy, (this.desc = true));
                    } else {
                        this.showGenericErrorToast();
                    }
                } catch (err) {
                    this.showGenericErrorToast();
                } finally {
                    this.isAPISubmitLoading = false;
                    this.damSireIdToDelete = null;
                }
            }
        },
        async copyDocumentUrl(document) {
            if (!document || this.copyTextContent === "Copied!") {
                return;
            }

            // Clear any existing timeout
            if (this.copyTimeout) {
                clearTimeout(this.copyTimeout);
            }

            const documentUrl = document.fileURL;

            try {
                // Copy the document URL to the clipboard
                await navigator.clipboard.writeText(documentUrl);
                this.copyTextContent = "Copied!";

                // Manually show the tooltip after copying
                this.$nextTick(() => {
                    const tooltipEl = this.$refs[`tooltip-${document.id}`];
                    if (tooltipEl && tooltipEl.length) {
                        $(tooltipEl[0]).tooltip("show");
                    }
                });
            } catch (error) {
                this.copyTextContent = "Failed to copy";
            }

            // Reset the tooltip text after 2 seconds
            this.copyTimeout = setTimeout(() => {
                this.copyTextContent = "Copy to Clipboard";

                // Hide the tooltip after the reset
                this.$nextTick(() => {
                    const tooltipEl = this.$refs[`tooltip-${document.id}`];
                    if (tooltipEl && tooltipEl.length) {
                        $(tooltipEl[0]).tooltip("hide");
                    }
                });
            }, 1500);
        },
        simplifyFileType(fileType) {
            const conversion = {
                "application/pdf": "pdf",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "doc",
                "image/png": "png",
                "image/jpeg": "jpeg",
                "image/jpg": "jpeg",
            };

            return conversion[fileType];
        },
        displayDate(date) {
            return this.formatDate(date);
        },
        clearFileInput(event) {
            event.target.value = "";
        },
    },
};
